import React, { useState, useEffect } from 'react';
import './LeftNavbar.css';
import favicon1 from '../assets/favicon1.png';
import {
  Users,
  ShieldCheck,
  ShoppingCart,
  FileText,
  LogOut,
  User,
  Menu,
  ChevronLeft,
  Settings,
  BarChart3,
} from 'lucide-react';
import { WarehouseOutlined } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Logo } from './Logo';
import { LogoutButton } from './LogoutButton';

export const LeftNavbar: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();

  const navigationSections = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      rolesAllowed: ['practitioner'],
      path: '/dashboard',
    },
    //   { id: "providers", label: "Providers", icon: UserPlus, rolesAllowed:["admin","manufacturer","practitioner","insurance"] , path: '/providers'},
    {
      id: 'demographics',
      label: 'Demographics',
      icon: Users,
      rolesAllowed: ['practitioner'],
      path: '/demographics',
    },
    {
      id: 'product-management',
      label: 'Product Management',
      icon: WarehouseOutlined,
      rolesAllowed: ['admin', 'manufacturer'],
      rolesRequiringExplicitPermission: ['admin'], // Admin needs explicit permission, manufacturer can use role
      path: '/product-management',
    },
    // { id: "inventory-dashboard", label: "Inventory Dashboard", icon: Users, rolesAllowed:["admin"] , path: '/inventory-dashboard'},
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCart,
      rolesAllowed: ['manufacturer', 'practitioner'],
      path: '/orders',
    },
    {
      id: 'insurance',
      label: 'Insurance',
      icon: ShieldCheck,
      rolesAllowed: ['insurance'],
      path: '/insurance',
    },
    // { id: "verifications", label: "Verifications", icon: CheckCircle, rolesAllowed:["practitioner","insurance"] , path: '/verifications'},
    {
      id: 'invoices',
      label: 'Invoicing',
      icon: FileText,
      rolesAllowed: ['manufacturer', 'practitioner'],
      path: '/invoices',
    },
    //   { id: "agreements", label: "Agreement Details", icon: Calendar, rolesAllowed:["admin","manufacturer","practitioner","insurance"] , path: '/agreements'},
    //   { id: "analytics", label: "Analytics", icon: TrendingUp, rolesAllowed:["admin","manufacturer","practitioner","insurance"] , path: '/analytics'},
    {
      id: 'user-management',
      label: 'User Management',
      icon: User,
      rolesAllowed: ['admin'],
      path: '/user-management',
    },
    // HIDDEN: Configuration module - Keep code for future reuse
    // {
    //   id: 'configuration',
    //   label: 'Configuration',
    //   icon: Cog,
    //   rolesAllowed: ['admin'],
    //   path: '/configuration',
    // },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      rolesAllowed: [
        'admin',
        'manufacturer',
        'practitioner',
        'insurance',
        'insurance_verifier',
      ],
      path: '/settings',
    },
  ];

  const [activeSection, setActiveSection] = useState(() => {
    // Set initial active section based on current location
    const currentPath = location.pathname;
    const currentSection = navigationSections.find(section => section.path === currentPath);
    return currentSection?.id || 'dashboard';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const getNavItems = () => {
    if (!user || !user.permissions) return [];

    // Map permission names to section IDs
    const permissionToSectionMap: Record<string, string> = {
      user_management: 'user-management',
      settings: 'settings',
      product_management: 'product-management',
      demographics: 'demographics',
      orders: 'orders',
      invoices: 'invoices',
      insurance: 'insurance',
    };

    return navigationSections.filter(section => {
      // Check if user has permission for this section
      const hasPermission = user.permissions.some(permission => {
        const mappedSectionId = permissionToSectionMap[permission];
        return mappedSectionId === section.id;
      });

      // Check if this section has role-specific permission requirements
      if (section.rolesRequiringExplicitPermission && section.rolesRequiringExplicitPermission.includes(user.role)) {
        // This role requires explicit permission (e.g., admin for Product Management)
        return hasPermission;
      }

      // For other roles, check role-based access as fallback
      const hasRoleAccess = section.rolesAllowed.includes(user.role);

      return hasPermission || hasRoleAccess;
    });
    // switch (user?.role) {
    //   case 'admin':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('admin'));
    //   case 'manufacturer':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('manufacturer'));
    //   case 'practitioner':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('practitioner'));
    //   case 'insurance':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('insurance'));
    //   default:
    //     return navigationSections.filter(section => section.rolesAllowed.includes('admin'));
    // }
  };

  const navItems = getNavItems();

  // Update active section when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    const currentSection = navigationSections.find(section => section.path === currentPath);
    if (currentSection) {
      setActiveSection(currentSection.id);
    }
  }, [location.pathname]);

  // Remove handleLogout function as we'll use LogoutButton component

  return (
    <div className="left-navbar-container">
      {/* Left Navigation Sidebar */}
      <div className={`left-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Header */}
        <div
          className={`sidebar-header ${isSidebarCollapsed ? 'collapsed' : ''}`}
        >
          {isSidebarCollapsed ? (
            <div className="collapsed-header-content">
              <div className="logo-container sidebar-logo">
                <div className="logo-container logo-icon" >
                  <img 
                    src={favicon1}
                    alt="MPM Medical Logo"
                    className="mpm-logo"
                    style={{ objectFit: 'contain' }}
                  />
                </div>
              </div>
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="toggle-button"
                title="Expand navigation"
              >
                <Menu className="toggle-icon" />
              </button>
            </div>
          ) : (
            <div className="expanded-header-content">
              <div className="sidebar-logo">
                <Logo size="small" />
              </div>
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="toggle-button"
                title="Collapse navigation"
              >
                <ChevronLeft className="toggle-icon" />
              </button>
            </div>
          )}
        </div>

        {/* User Profile Section */}
        <div
          className={`user-profile-section ${isSidebarCollapsed ? 'collapsed' : ''}`}
          data-tooltip={`${user?.name} (${user?.role})`}
        >
          <div className="user-profile-content">
            <div className="user-avatar">
              <User className="user-avatar-icon" />
            </div>
            {!isSidebarCollapsed && (
              <div>
                <p className="user-name text-subtitle">{user?.name}</p>
                <p className="user-role text-paragraph">{user?.role}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <div className="navigation-container">
          <nav
            className={`navigation-menu ${isSidebarCollapsed ? 'collapsed' : ''}`}
          >
            {navItems.map(section => (
              <Link
                key={section.id}
                to={section.path}
                className={`nav-link ${location.pathname === section.path ? 'active' : ''}`}
              >
                <div
                  className={`nav-item ${isSidebarCollapsed ? 'collapsed' : ''} ${
                    activeSection === section.id ? 'active' : ''
                  }`}
                  title={isSidebarCollapsed ? section.label : undefined}
                  onClick={() => setActiveSection(section.id)}
                >
                  <section.icon
                    className={`nav-icon ${isSidebarCollapsed ? 'centered' : ''}`}
                  />
                  {!isSidebarCollapsed && (
                    <span className="nav-label nav-item">{section.label}</span>
                  )}
                </div>
              </Link>
            ))}
          </nav>
        </div>

        {/* Logout Button - Positioned at the bottom */}
        <div
          className={`logout-section ${isSidebarCollapsed ? 'collapsed' : ''}`}
        >
          <LogoutButton className="logout-button-bottom">
            <LogOut className="logout-icon" />
            {!isSidebarCollapsed && <span className="logout-text">Logout</span>}
          </LogoutButton>
        </div>
      </div>
    </div>
  );
};
